import React, { useState, useRef, useEffect } from 'react'
import { API_AUTH_TYPES, API_ROUTES, ApiRequest } from '../../../../../api';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import Message from './Message';
import { PencilIcon, ChevronUpIcon } from '@heroicons/react/outline';
import { useParams } from 'react-router-dom/cjs/react-router-dom.min';
import UndoDelete from './UndoDelete';

const bunmailApi = new ApiRequest(API_ROUTES.BUNMAIL, API_AUTH_TYPES.ADVISOR);

// Separate component for draft items to follow React hooks rules
const DraftItem = ({ id, onSelect, index, handleKeyDown }) => {
  const { data: draft, isLoading: isDraftLoading } = useQuery({
    queryFn: bunmailApi.getFn({
      endpoint: 'get_message',
      params: [id],
    }),
    queryKey: ['email', id],
    enabled: !!id,
  });

  return (
    <div
      id={`draft-item-${index}`}
      onClick={() => onSelect(id)}
      onKeyDown={(e) => handleKeyDown(e, id, index)}
      className="p-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 flex items-center"
      tabIndex="0"
      role="menuitem"
    >
      <div className="flex-1 truncate">
        {isDraftLoading ? (
          <p className="text-sm text-gray-400">Loading...</p>
        ) : (
          <>
            <p className="text-sm font-medium">{draft?.subject || 'New Message'}</p>
            <p className="text-xs text-gray-500 truncate">
              {draft?.to_email || 'No recipients'}
            </p>
          </>
        )}
      </div>
    </div>
  );
};

const EmailMessages = () => {
  const params = useParams()
  const [showDraftsMenu, setShowDraftsMenu] = useState(false);
  const [draftOrder, setDraftOrder] = useState([]);
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);
  const queryClient = useQueryClient();

  // Undo delete state
  const [undoState, setUndoState] = useState({ show: false, timer: null, draftId: null });

  const { data: draftIds, isLoading, isError } = useQuery({
    queryFn: bunmailApi.getFn({
      endpoint: 'index_drafts_being_composed',
    }),
    queryKey: ['drafts_being_composed'],
  });

  // Delete draft mutation
  const deleteDraftMutation = useMutation(
    (draftId) => bunmailApi.delete({ endpoint: 'delete_draft', params: [draftId] }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('drafts_being_composed');
      }
    }
  );

  const triggerDeleteDraft = (draftId) => {
    // Optimistically remove from UI
    queryClient.setQueryData(['drafts_being_composed'], (old) => old?.filter(d => d.id !== draftId) || []);

    // Clear previous timer
    if (undoState.timer) clearTimeout(undoState.timer);

    const newTimer = setTimeout(() => {
      deleteDraftMutation.mutate(draftId);
      setUndoState({ show: false, timer: null, draftId: null });
    }, 5000); // 5 seconds to undo

    setUndoState({ show: true, timer: newTimer, draftId: draftId });
  };

  const handleUndoDelete = () => {
    if (undoState.timer) clearTimeout(undoState.timer);

    // Invalidate to refetch and bring back the removed draft.
    queryClient.invalidateQueries('drafts_being_composed');

    setUndoState({ show: false, timer: null, draftId: null });
  };

  const handleConfirmDelete = () => {
    if (undoState.timer) clearTimeout(undoState.timer);

    deleteDraftMutation.mutate(undoState.draftId);
    setUndoState({ show: false, timer: null, draftId: null });
  };

  // Improved draft order management
  useEffect(() => {
    if (!draftIds) return;

    // Get current existing draft IDs from API
    const apiDraftIds = draftIds.map(draft => draft.id);

    setDraftOrder(prevOrder => {
      // Filter out drafts that no longer exist in the API
      const filteredOrder = prevOrder.filter(id => apiDraftIds.includes(id));

      // Find new drafts that aren't in our current order
      const newDraftIds = apiDraftIds.filter(id => !prevOrder.includes(id));

      // If we have filtered order (meaning some existing drafts are still valid)
      if (filteredOrder.length > 0) {
        // Add any new drafts to the end
        return [...filteredOrder, ...newDraftIds];
      } else {
        // If no existing drafts remain or this is initial load, use API order
        return apiDraftIds;
      }
    });
  }, [draftIds]);

  const { mutate: createDraft } = useMutation(
    async () => bunmailApi.post({
      endpoint: 'create_draft',
      body: {
        is_being_composed: true,
        to_email: '',
        from_email: '',
        from_name: '',
        household_id: params.household_id,
      },
    }),
    {
      onSuccess: (data) => {
        // invalidate the drafts being composed query
        queryClient.invalidateQueries('drafts_being_composed');
      },
      onError: (error) => {
        // Handle error
        console.error(error);
      },
    }
  )


  // Function to promote a draft to the top of the order
  const promoteDraft = (id) => {
    setDraftOrder(prevOrder => {
      // First make sure the ID exists in our order
      if (!prevOrder.includes(id)) return prevOrder;

      // Remove the draft from its current position and add to front
      const newOrder = prevOrder.filter(draftId => draftId !== id);
      return [id, ...newOrder];
    });

    // Close the dropdown menu
    setShowDraftsMenu(false);
  };

  // Get visible drafts (first 2 in order)
  const visibleDraftIds = draftOrder
    .filter(id => draftIds?.some(d => d.id === id))
    .slice(0, 2)
    .map(id => ({ id }));

  // Get additional drafts (rest of order)
  const additionalDraftIds = draftOrder
    .filter(id => draftIds?.some(d => d.id === id))
    .slice(2)
    .map(id => ({ id }));

  const hasAdditionalDrafts = additionalDraftIds.length > 0;

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target)
      ) {
        setShowDraftsMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [dropdownRef, buttonRef]);

  // Handle keyboard navigation
  const handleKeyDown = (e, id, index) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      promoteDraft(id);
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      const nextElement = document.getElementById(`draft-item-${index + 1}`);
      if (nextElement) nextElement.focus();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      const prevElement = document.getElementById(`draft-item-${index - 1}`);
      if (prevElement) prevElement.focus();
      else buttonRef.current?.focus();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setShowDraftsMenu(false);
      buttonRef.current?.focus();
    }
  };

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (undoState.timer) {
        clearTimeout(undoState.timer);
      }
    };
  }, [undoState.timer]);

  return (
    <>
      {undoState.show && (
        <UndoDelete
          undoFunction={handleUndoDelete}
          closeFunction={handleConfirmDelete}
          emailCount={1}
        />
      )}
      <div className='fixed right-24 bottom-0 z-50 '>
        <div className="flex space-x-5 items-end">
          {visibleDraftIds.map(({ id }) => (
            <div key={id}>
              <Message id={id} onDeleteDraft={triggerDeleteDraft} />
            </div>
          ))}

          {/* Additional Drafts Menu */}
          {hasAdditionalDrafts && (
            <div className="relative mb-6">
              <button
                ref={buttonRef}
                onClick={() => setShowDraftsMenu(!showDraftsMenu)}
                className="btn-primary-rounded p-2 flex items-center"
                aria-haspopup="true"
                aria-expanded={showDraftsMenu}
              >
                <span className="mr-1">{additionalDraftIds.length}+</span>
                <ChevronUpIcon className={`h-4 w-4 transition-transform ${showDraftsMenu ? '' : 'transform rotate-180'}`} />
              </button>

              {showDraftsMenu && (
                <div
                  ref={dropdownRef}
                  className="absolute bottom-full mb-2 right-0 w-64 bg-white rounded-lg border border-gray-200 shadow-lg overflow-hidden"
                  role="menu"
                >
                  <div className="p-2 bg-gray-100 border-b border-gray-200">
                    <h3 className="text-sm font-medium">Additional Drafts</h3>
                  </div>
                  <div className="max-h-60 overflow-y-auto">
                    {additionalDraftIds.map(({ id }, index) => (
                      <DraftItem
                        key={id}
                        id={id}
                        index={index}
                        onSelect={promoteDraft}
                        handleKeyDown={handleKeyDown}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          <button onClick={() => createDraft()} className="btn-gray-rounded mb-6 p-2"><PencilIcon className='h-5 w-5' /></button>
        </div>
      </div>
    </>
  )
}

export default EmailMessages